import { Group, ScrollArea, Stack } from "@mantine/core"
import _ from "lodash"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { FindOperationChainsResponseDto } from "~/gen/api"
import {
  HorizontalDivider,
  VerticalDivider,
} from "~/pages/Brand/Products/Details/TraceabilityGraph/Divider.tsx"
import { TraceabilityTreeBranch } from "~/pages/Brand/Products/Details/TraceabilityGraph/TraceabilityTreeBranch.tsx"

type Props = {
  product: LibraryResponses.ProductDetails
  operationsChains: {
    productOperationChains: FindOperationChainsResponseDto[]
    componentOperationChains: FindOperationChainsResponseDto[]
    materialOperationChains: FindOperationChainsResponseDto[]
  }
}

export const ProductTraceability = ({ product, operationsChains }: Props) => {
  const productItem = product.item

  const sortedComponents = _.sortBy(
    product.components,
    (component) => -component.mainFabric
  )

  const verticalDividerCoveredCardsCount = _.reduce(
    sortedComponents,
    (sum, component, index) => {
      if (index === product.components.length - 1) {
        return sum + 1
      }
      return sum + (component.materials.length || 1)
    },
    0
  )

  return (
    <ScrollArea w="100%" type="scroll" offsetScrollbars>
      <Group
        gap={0}
        align="flex-start"
        style={{ position: "relative" }}
        mb="xl"
        wrap="nowrap"
      >
        {operationsChains.productOperationChains
          ?.find((opChain) => opChain.name === product.type.operationChain.name)
          ?.activities.map(({ name: activityName }, index) => (
            <TraceabilityTreeBranch
              hideFirstDivider={index === 0}
              key={`${product.id}-${activityName}`}
              activityName={activityName}
              item={productItem}
            />
          ))}
        {!isComponentOperationChainEmpty(
          product.components[0],
          operationsChains.componentOperationChains
        ) ? (
          <>
            <HorizontalDivider />
            <Stack align="flex-start" justify="left">
              {product.components.length > 1 && (
                <VerticalDivider itemCount={verticalDividerCoveredCardsCount} />
              )}
              {sortedComponents.map((component) => (
                <Group
                  key={component.id}
                  gap={0}
                  align="flex-start"
                  wrap="nowrap"
                >
                  {operationsChains.componentOperationChains
                    ?.find(
                      (opChain) =>
                        opChain.name === component.operationChain.name
                    )
                    ?.activities.map(({ name: activityName }) => (
                      <TraceabilityTreeBranch
                        key={`${component.id}-${activityName}`}
                        activityName={activityName}
                        item={component.item}
                      />
                    ))}
                  {component.materials.length > 0 && <HorizontalDivider />}
                  <Stack align="flex-start" justify="left">
                    {component.materials.length > 1 && (
                      <VerticalDivider itemCount={component.materials.length} />
                    )}
                    {component.materials.map((material) => (
                      <Group
                        key={material.id}
                        gap={0}
                        align="flex-start"
                        wrap="nowrap"
                      >
                        {operationsChains.materialOperationChains
                          ?.find(
                            (opChain) =>
                              opChain.name ===
                              component.operationChain?.linkedOperationChain
                                ?.name
                          )
                          ?.activities.map(({ name: activityName }) => (
                            <TraceabilityTreeBranch
                              key={`${material.id}-${activityName}`}
                              activityName={activityName}
                              item={material.item}
                            />
                          ))}
                      </Group>
                    ))}
                  </Stack>
                </Group>
              ))}
            </Stack>
          </>
        ) : null}
      </Group>
    </ScrollArea>
  )
}

function isComponentOperationChainEmpty(
  component: LibraryResponses.EnrichedComponent,
  operationsChains: FindOperationChainsResponseDto[]
) {
  return (
    operationsChains.find(
      (opChain) => opChain.name === component.operationChain.name
    )?.activities.length === 0
  )
}
